import { countsTowardGate } from "./evidence-attach";
import type { EightDStage, EvidenceItem, ProblemRecord, WorkflowStage } from "./types";

/**
 * Package #2.4 — links the 11-stage workflow and the 8D chapter so they can
 * never disagree. The mapping is reference data (policy setting
 * `workflow.eightd_mapping`); DEFAULT_EIGHTD_MAPPING is the fallback.
 */

export interface SharedEvidencePair {
  /** Requirement name on the workflow stage. */
  stageEvidence: string;
  dStep: string;
  /** Requirement name on the 8D step. */
  dEvidence: string;
}

export interface StageLinkEntry {
  stage: number;
  dSteps: string[];
  shared: SharedEvidencePair[];
}

export interface EightDMapping {
  links: StageLinkEntry[];
}

const d = (id: string, title: string) => `${id} deliverable — ${title}`;

export const DEFAULT_EIGHTD_MAPPING: EightDMapping = {
  links: [
    { stage: 1, dSteps: [], shared: [] },
    { stage: 2, dSteps: [], shared: [] },
    { stage: 3, dSteps: [], shared: [] },
    {
      stage: 4,
      dSteps: ["D1", "D2"],
      shared: [
        {
          stageEvidence: "Governance assignment record",
          dStep: "D1",
          dEvidence: d("D1", "Establish the Team"),
        },
        {
          stageEvidence: "Problem statement (is / is-not)",
          dStep: "D2",
          dEvidence: d("D2", "Describe the Problem"),
        },
      ],
    },
    {
      stage: 5,
      dSteps: ["D3"],
      shared: [
        {
          stageEvidence: "Containment work instruction",
          dStep: "D3",
          dEvidence: d("D3", "Interim Containment"),
        },
      ],
    },
    {
      stage: 6,
      dSteps: ["D4"],
      shared: [
        {
          stageEvidence: "Root cause proof package",
          dStep: "D4",
          dEvidence: d("D4", "Root Cause Analysis"),
        },
      ],
    },
    {
      stage: 7,
      dSteps: ["D5"],
      shared: [
        {
          stageEvidence: "Change request approval",
          dStep: "D5",
          dEvidence: d("D5", "Permanent Corrective Actions"),
        },
      ],
    },
    {
      stage: 8,
      dSteps: ["D6"],
      shared: [
        {
          stageEvidence: "Implementation audit",
          dStep: "D6",
          dEvidence: d("D6", "Implement & Validate"),
        },
      ],
    },
    {
      stage: 9,
      dSteps: ["D7"],
      shared: [
        {
          stageEvidence: "Revised PFMEA sheet",
          dStep: "D7",
          dEvidence: d("D7", "Prevent Recurrence"),
        },
      ],
    },
    { stage: 10, dSteps: [], shared: [] },
    {
      stage: 11,
      dSteps: ["D8"],
      shared: [
        {
          stageEvidence: "Closure sign-off",
          dStep: "D8",
          dEvidence: d("D8", "Recognise the Team"),
        },
      ],
    },
  ],
};

export function normalizeMapping(value: unknown): EightDMapping {
  const v = value as EightDMapping | null;
  if (v && Array.isArray(v.links)) return v;
  return DEFAULT_EIGHTD_MAPPING;
}

const entryFor = (m: EightDMapping, stageIndex: number) =>
  m.links.find((l) => l.stage === stageIndex);

export function linkedDSteps(
  record: ProblemRecord,
  stage: WorkflowStage,
  m: EightDMapping,
): EightDStage[] {
  const ids = entryFor(m, stage.index)?.dSteps ?? [];
  return record.eightD.filter((x) => ids.includes(x.id));
}

export function stageForD(
  record: ProblemRecord,
  dId: string,
  m: EightDMapping,
): WorkflowStage | undefined {
  const link = m.links.find((l) => l.dSteps.includes(dId));
  return link ? record.stages.find((s) => s.index === link.stage) : undefined;
}

export interface SharedHit {
  item: EvidenceItem;
  /** "Stage 6" or "D4" — where the item was attached. */
  from: string;
}

/** Real evidence for a requirement, own gate first, then the linked partner. */
export function evidenceFor(
  record: ProblemRecord,
  gate: { kind: "stage"; id: string } | { kind: "d"; id: string },
  req: string,
  m: EightDMapping,
): { own?: EvidenceItem; shared?: SharedHit } {
  if (gate.kind === "stage") {
    const stage = record.stages.find((s) => s.id === gate.id);
    if (!stage) return {};
    const own = stage.evidence.find((e) => e.name === req && countsTowardGate(e));
    if (own) return { own };
    const pair = entryFor(m, stage.index)?.shared.find((p) => p.stageEvidence === req);
    if (!pair) return {};
    const dStep = record.eightD.find((x) => x.id === pair.dStep);
    const item = dStep?.evidence.find((e) => e.name === pair.dEvidence && countsTowardGate(e));
    return item ? { shared: { item, from: pair.dStep } } : {};
  }
  const dStep = record.eightD.find((x) => x.id === gate.id);
  if (!dStep) return {};
  const own = dStep.evidence.find((e) => e.name === req && countsTowardGate(e));
  if (own) return { own };
  const stage = stageForD(record, gate.id, m);
  const pair =
    stage &&
    entryFor(m, stage.index)?.shared.find((p) => p.dStep === gate.id && p.dEvidence === req);
  if (!stage || !pair) return {};
  const item = stage.evidence.find((e) => e.name === pair.stageEvidence && countsTowardGate(e));
  return item ? { shared: { item, from: `Stage ${stage.index}` } } : {};
}

export function missingStageEvidence(
  record: ProblemRecord,
  stage: WorkflowStage,
  m: EightDMapping,
): string[] {
  return stage.requiredEvidence.filter((req) => {
    const hit = evidenceFor(record, { kind: "stage", id: stage.id }, req, m);
    return !hit.own && !hit.shared;
  });
}

export function missingDEvidence(
  record: ProblemRecord,
  dStep: EightDStage,
  m: EightDMapping,
): string[] {
  return dStep.requiredEvidence.filter((req) => {
    const hit = evidenceFor(record, { kind: "d", id: dStep.id }, req, m);
    return !hit.own && !hit.shared;
  });
}

export interface StageGate {
  missing: string[];
  openD: string[];
  blocked: boolean;
  message: string | null;
}

export function stageGate(
  record: ProblemRecord,
  stage: WorkflowStage,
  m: EightDMapping,
): StageGate {
  const missing = missingStageEvidence(record, stage, m);
  const openD = linkedDSteps(record, stage, m)
    .filter((x) => x.status !== "complete")
    .map((x) => x.id);
  const parts: string[] = [];
  if (missing.length) parts.push(`${missing.length} required evidence item(s) outstanding`);
  if (openD.length) parts.push(`waiting on ${openD.join(", ")} in the 8D`);
  return {
    missing,
    openD,
    blocked: parts.length > 0,
    message: parts.length ? `Stage ${stage.index} cannot close yet — ${parts.join("; ")}.` : null,
  };
}

export interface DGate {
  missing: string[];
  blocked: boolean;
  message: string | null;
}

export function dGate(record: ProblemRecord, dStep: EightDStage, m: EightDMapping): DGate {
  const missing = missingDEvidence(record, dStep, m);
  if (missing.length)
    return { missing, blocked: true, message: `${missing.length} deliverable(s) outstanding.` };
  const own = stageForD(record, dStep.id, m);
  if (own) {
    for (const s of record.stages) {
      if (s.index >= own.index || s.status === "complete") continue;
      const open = linkedDSteps(record, s, m).filter(
        (x) => x.status !== "complete" && x.id !== dStep.id,
      );
      if (open.length) {
        return {
          missing,
          blocked: true,
          message: `${dStep.id} can't be completed yet: Stage ${s.index} (${s.name}) comes earlier in the workflow and its ${open
            .map((x) => x.id)
            .join(
              ", ",
            )} is still open. Complete ${open[0]!.id} first so the 8D and the workflow stay in the same order.`,
        };
      }
    }
  }
  return { missing, blocked: false, message: null };
}

/** Stage ready to close after its last linked D step completed (never auto-completes). */
export function readyToClose(
  record: ProblemRecord,
  stage: WorkflowStage,
  m: EightDMapping,
): boolean {
  if (stage.status === "complete") return false;
  if (linkedDSteps(record, stage, m).length === 0) return false;
  return !stageGate(record, stage, m).blocked;
}

export function dueConflict(dStep: EightDStage, stage: WorkflowStage | undefined): boolean {
  return !!stage && dStep.dueDate.slice(0, 10) > stage.dueDate.slice(0, 10);
}

export function validateDDueDate(
  record: ProblemRecord,
  dId: string,
  date: string,
  m: EightDMapping,
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}/.test(date)) return "Enter a valid due date.";
  const stage = stageForD(record, dId, m);
  if (stage && date.slice(0, 10) > stage.dueDate.slice(0, 10))
    return `${dId} cannot be due after Stage ${stage.index} (${stage.name}), which is due ${stage.dueDate.slice(0, 10)}.`;
  return null;
}

export interface Inconsistency {
  target: string; // stage id or D id
  explanation: string;
}

/** Disagreements recorded before the views were linked. Read-only: nothing is changed. */
export function findInconsistencies(record: ProblemRecord, m: EightDMapping): Inconsistency[] {
  const out: Inconsistency[] = [];
  for (const s of record.stages) {
    const open = linkedDSteps(record, s, m).filter((x) => x.status !== "complete");
    if (s.status === "complete" && open.length)
      out.push({
        target: s.id,
        explanation: `Stage ${s.index} was completed while ${open.map((x) => x.id).join(", ")} was still open.`,
      });
  }
  for (const x of record.eightD) {
    const s = stageForD(record, x.id, m);
    if (!s) continue;
    if (x.status === "complete" && s.status === "not-started")
      out.push({
        target: x.id,
        explanation: `${x.id} is complete but Stage ${s.index} has not started.`,
      });
    if (dueConflict(x, s))
      out.push({
        target: x.id,
        explanation: `${x.id} is due ${x.dueDate.slice(0, 10)}, after Stage ${s.index} (due ${s.dueDate.slice(0, 10)}).`,
      });
  }
  return out;
}
